import { useState, useEffect, useRef } from "preact/hooks"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet"
import { Button } from "../ui/button"
import { Textarea } from "../ui/textarea"
import { MessageList } from "./MessageList"
import { ConversationSelector } from "./ConversationSelector"
import {
  confirmForecastAction,
  Conversation,
  ForecastConfirmationAction,
  Message,
  sendMessageStream,
  getMessages,
} from "../../api/forecast_chat"
import { ForecastVersion } from "../../dashboard/sales_forecasting/types"
import { ArrowUp, Wand2 } from "lucide-react"
import { toast } from "../ui/use-toast"

interface ChatPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  forecastVersions: ForecastVersion[]
  currentForecastVersionId?: string
  onForecastChanged?: () => void
  className?: string
}

export function ChatPanel({
  open,
  onOpenChange,
  forecastVersions,
  currentForecastVersionId,
  onForecastChanged,
  className,
}: ChatPanelProps) {
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState("")
  const [sending, setSending] = useState(false)
  const [confirmingAction, setConfirmingAction] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [streamingStatus, setStreamingStatus] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const streamAbortRef = useRef<AbortController | null>(null)
  // The conversation currently shown; async completions check it so a slow response for a
  // previously selected conversation can't overwrite the newly selected one's messages.
  const activeConversationIdRef = useRef<string | null>(null)

  async function loadMessages(conversationId: string) {
    setLoadingMessages(true)
    try {
      const msgs = await getMessages(conversationId)
      if (activeConversationIdRef.current !== conversationId) return
      setMessages(msgs)
    } catch (error) {
      console.error("Failed to load messages:", error)
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive",
      })
    } finally {
      setLoadingMessages(false)
    }
  }

  // Load messages when conversation changes or panel opens
  useEffect(() => {
    activeConversationIdRef.current = conversation?.id ?? null
    if (conversation && open) {
      loadMessages(conversation.id)
    } else if (!conversation) {
      setMessages([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, open])

  function handleConversationChange(conv: Conversation | null) {
    // Abandon any in-flight response for the previous conversation.
    streamAbortRef.current?.abort()
    setConversation(conv)
    // Clear input when conversation changes
    if (conv) {
      setInputValue("")
    }
  }

  async function handleSendMessage() {
    if (!inputValue.trim() || !conversation || sending) return

    const userMessageContent = inputValue.trim()
    const assistantMessageId = `stream-${Date.now()}`
    setInputValue("")
    setSending(true)
    setStreamingStatus("Reading the forecast context...")

    // Optimistically add user message
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: conversation.id,
      role: "user",
      content: userMessageContent,
      created_at: new Date().toISOString(),
    }
    const streamingAssistantMessage: Message = {
      id: assistantMessageId,
      conversation_id: conversation.id,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, tempUserMessage, streamingAssistantMessage])

    try {
      streamAbortRef.current?.abort()
      await new Promise<void>((resolve, reject) => {
        streamAbortRef.current = sendMessageStream(
          conversation.id,
          { content: userMessageContent },
          {
            onThinking: (message: string) => setStreamingStatus(message),
            // Generic on purpose: questions range from math checks to plan edits, so a
            // specific-sounding status ("looking up rows") is usually wrong.
            onThinkingDelta: () => setStreamingStatus("Thinking..."),
            onThinkingDone: () => setStreamingStatus("Working on it..."),
            onToolCall: (toolName: string) => {
              const status = getForecastToolStatus(toolName)
              setStreamingStatus(status)
              setMessages((prev) => prev.map((message: Message) => (
                message.id === assistantMessageId && !message.content
                  ? { ...message, content: `${status}...` }
                  : message
              )))
            },
            onToolResult: (_toolName: string, _result: unknown, confirmationRequired: boolean) => {
              setStreamingStatus(confirmationRequired ? "Preparing the proposed change..." : "Reviewing the data...")
            },
            onTextDelta: (content: string) => {
              setStreamingStatus("Writing the response...")
              setMessages((prev) => prev.map((message: Message) => (
                message.id === assistantMessageId
                  ? { ...message, content: isStatusPlaceholder(message.content) ? content : message.content + content }
                  : message
              )))
            },
            onText: (content: string) => {
              setMessages((prev) => prev.map((message: Message) => (
                message.id === assistantMessageId ? { ...message, content } : message
              )))
            },
            onDone: () => {
              setStreamingStatus("")
              resolve()
            },
            onAborted: () => resolve(),
            onError: (error: string) => reject(new Error(error)),
          }
        )
      })
      await loadMessages(conversation.id)
    } catch (error) {
      // Remove temp message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id && m.id !== assistantMessageId))
      // A deliberate abort (conversation switch / unmount) is not an error to surface.
      if (error instanceof DOMException && error.name === "AbortError") return
      console.error("Failed to send message:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message",
        variant: "destructive",
      })
    } finally {
      setSending(false)
      setStreamingStatus("")
      streamAbortRef.current = null
    }
  }

  async function handleConfirmAction(action: ForecastConfirmationAction) {
    if (!conversation || confirmingAction) return

    setConfirmingAction(true)
    try {
      await confirmForecastAction(conversation.id, action)
      await loadMessages(conversation.id)
      onForecastChanged?.()
      toast({
        title: "Forecast updated",
        description: "The forecast action was applied successfully.",
      })
    } catch (error) {
      console.error("Failed to confirm forecast action:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to apply forecast action",
        variant: "destructive",
      })
    } finally {
      setConfirmingAction(false)
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    // Enter sends (the expected chat behavior); Shift+Enter inserts a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  /** Starter prompts shown on an empty conversation so the first message writes itself. */
  const SUGGESTED_PROMPTS = [
    "What were the biggest drivers of change in the last 3 months?",
    "Summarize this plan: top customers and SKUs by revenue",
    "Which months look out of line with the rest of the year?",
    "Apply a seasonal dip: reduce Oct–Dec volume by 15% for one customer",
    "Delete every forecast for <product name or UPC> across all customers and summarize the volume and revenue impact",
    // Every input the sales-source builder needs, up front — the user swaps the
    // <placeholders> and sends one complete brief instead of a 10-turn interview.
    "Add a new sales source: customer <name>, product line <line>, SKU <sku or none>, " +
      "starting <2027-01>, <25> doors at <10> cases/store/month, $<12.50> per case, " +
      "<flat | winter-dip> seasonality, <100% committed | 50% opportunity named \"...\">",
  ]

  function handleSuggestedPrompt(prompt: string) {
    setInputValue(prompt)
    textareaRef.current?.focus()
  }

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto"
      const scrollHeight = textarea.scrollHeight
      const lineHeight = 24 // Approximate line height in pixels
      const minHeight = lineHeight + 16 // Single line + padding
      const maxHeight = lineHeight * 8 + 16 // Max 8 lines + padding
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)
      textarea.style.height = `${newHeight}px`
      
      // Enable scrolling if content exceeds max height
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = "auto"
      } else {
        textarea.style.overflowY = "hidden"
      }
    }
  }, [inputValue])

  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort()
    }
  }, [])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" class={`w-full sm:w-[600px] flex flex-col p-0 ${className || ""}`}>
        <div class="flex flex-col h-full max-h-screen">
          <SheetHeader class="p-4 border-b flex-shrink-0">
            <SheetTitle class="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-blue-700" />
              ForecastWiz
            </SheetTitle>
            <div class="mt-4">
              <ConversationSelector
                forecastVersions={forecastVersions}
                currentForecastVersionId={currentForecastVersionId}
                onConversationChange={handleConversationChange}
              />
            </div>
          </SheetHeader>

          <div class="flex-1 min-h-0 overflow-y-auto">
            <MessageList
              messages={messages}
              loading={sending || loadingMessages}
              confirmingAction={confirmingAction}
              streamingStatus={streamingStatus}
              onConfirmAction={handleConfirmAction}
            />
          </div>

          <div class="p-4 pb-20 border-t flex-shrink-0">
            {conversation && !loadingMessages && !sending && messages.length === 0 && (
              <div class="mb-3">
                <p class="mb-2 text-xs font-medium text-gray-500">Try asking</p>
                <div class="flex flex-wrap gap-2">
                  {SUGGESTED_PROMPTS.map((prompt: string) => (
                    <button
                      key={prompt}
                      type="button"
                      class="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-left text-xs text-blue-800 hover:bg-blue-100"
                      onClick={() => handleSuggestedPrompt(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div class="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={inputValue}
                onInput={(e: any) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about this forecast or request an edit... (Enter to send, Shift+Enter for a new line)"
                class="resize-none !min-h-[40px]"
                rows={1}
                disabled={!conversation || sending}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!conversation || !inputValue.trim() || sending}
                variant="default"
                class="self-start mt-1"
              >
                {sending ? (
                  <span class="inline-block h-4 w-4 rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground animate-spin" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
              </Button>
            </div>
            {!conversation && (
              <p class="text-sm text-gray-500 mt-2">
                {forecastVersions.length === 0 
                  ? "Create a forecast version to start chatting."
                  : "Start a new chat or pick a recent conversation above."}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function getForecastToolStatus(toolName: string): string {
  switch (toolName) {
    case "list_forecast_versions":
      return "Checking available forecast versions"
    case "get_forecast_summary":
      return "Reading forecast totals"
    case "get_forecast_hierarchy_slice":
      return "Finding the relevant customer and SKU rows"
    case "diff_forecast_versions":
      return "Comparing forecast versions"
    case "preview_forecast_edits":
      return "Previewing the forecast change"
    case "preview_clone_forecast_version":
      return "Previewing a new forecast version"
    case "preview_sales_source_creation":
      return "Previewing the new sales source"
    default:
      return "Checking forecast data"
  }
}

function isStatusPlaceholder(content: string): boolean {
  return content.endsWith("...") && (
    content.startsWith("Checking") ||
    content.startsWith("Reading") ||
    content.startsWith("Finding") ||
    content.startsWith("Comparing") ||
    content.startsWith("Previewing")
  )
}

